import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEdit, faSpinner, faCheck, faTimes, faLink, faCopy, faTrash, faExclamationTriangle, faHistory, faEye, faFlag, faEnvelope, faLightbulb, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../context/AuthContext';
import { WatchlistContext } from '../../context/WatchlistContext';
import ContentCard from '../Cards/ContentCard';
import { UserComments } from '../Comments';
import UserReports from './UserReports';
import { UserAPI } from '../../api';
import { useNavigate } from 'react-router-dom';
import UserSuggestions from './UserSuggestions';

const ProfileContainer = styled.div`
  padding: 100px 50px 30px; /* Added top padding for header */
  
  @media (max-width: 768px) {
    padding: 100px 20px 20px; /* Added top padding for header */
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const AvatarContainer = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--background-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 30px;
  border: 3px solid var(--primary-light);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    font-size: 60px;
    color: var(--text-dim);
  }
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 20px;
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const Username = styled.h1`
  font-size: 32px;
  margin-bottom: 5px;
`;

const Email = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: var(--text-dim);
  margin-bottom: 10px;
  
  svg {
    margin-right: 8px;
    font-size: 14px;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    margin-bottom: 12px;
  }
`;

const FullName = styled.h2`
  font-size: 20px;
  color: var(--text-dim);
  margin-bottom: 10px;
`;

const Bio = styled.p`
  color: var(--text-dim);
  margin-bottom: 15px;
  max-width: 600px;
  line-height: 1.6;
`;

const ShareableLink = styled.div`
  margin-top: 15px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  
  span {
    font-size: 14px;
    color: var(--text-dim);
    margin-right: 10px;
    margin-bottom: 5px;
  }
  
  .link {
    background-color: rgba(255, 255, 255, 0.1);
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 14px;
    color: var(--primary-light);
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }
    
    svg {
      margin-left: 8px;
    }
  }
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 15px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const EditForm = styled.form`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-width: 500px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 5px;
  color: var(--text-dim);
`;

const Input = styled.input`
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  color: white;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light);
  }
`;

const Textarea = styled.textarea`
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  color: white;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light);
  }
`;

const AvatarSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const AvatarOption = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid ${props => props.selected ? 'var(--primary-light)' : 'transparent'};
  transition: all 0.2s;
  
  &:hover {
    transform: scale(1.05);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary-light);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
  
  svg {
    margin-right: 8px;
  }
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WatchlistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  
  h3 {
    font-size: 20px;
    margin-bottom: 10px;
  }
  
  p {
    color: var(--text-dim);
  }
`;

// Array of available avatars
const AVATARS = [
  'avatar1.jpeg',
  'avatar2.jpeg',
  'avatar3.jpeg',
  'avatar4.jpeg',
  'avatar5.jpeg',
  'avatar6.jpeg',
  'avatar7.jpeg',
  'avatar8.jpeg',
  'avatar9.jpeg',
  'avatar10.jpg',
  'avatar11.jpg',
  'avatar12.jpg',
  'default.jpeg'
];

const ActionButton = styled.button`
  background-color: ${props => props.danger ? 'rgba(220, 53, 69, 0.1)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.danger ? '#dc3545' : 'white'};
  border: 1px solid ${props => props.danger ? 'rgba(220, 53, 69, 0.2)' : 'rgba(255, 255, 255, 0.2)'};
  padding: 8px 15px;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: ${props => props.danger ? 'rgba(220, 53, 69, 0.2)' : 'rgba(255, 255, 255, 0.2)'};
  }
  
  svg {
    margin-right: 8px;
  }
`;

const DangerSection = styled.div`
  margin-top: 60px;
  padding: 20px;
  background-color: rgba(220, 53, 69, 0.05);
  border: 1px solid rgba(220, 53, 69, 0.1);
  border-radius: 10px;
`;

const DangerTitle = styled.h3`
  color: #dc3545;
  font-size: 20px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
  }
`;

const DangerText = styled.p`
  color: var(--text-dim);
  margin-bottom: 20px;
`;

const DangerButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`;

const WatchHistoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 15px;
  padding: 10px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const WatchHistoryItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px;
  border-radius: 10px;
  background-color: rgba(26, 26, 26, 0.8);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: rgba(42, 42, 42, 0.8);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  }
`;

const WatchHistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const WatchHistoryInfo = styled.div`
  flex: 1;
`;

const WatchHistoryTitle = styled.div`
  color: #fff;
  font-weight: 500;
  
  span { 
    display: block;
    font-size: 16px; 
    margin-bottom: 4px;
  }
`;

const WatchHistoryMeta = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-dim);
  font-size: 14px;
  flex-wrap: wrap;
  
  span {
    margin-right: 15px;
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    
    svg {
      margin-right: 5px;
      font-size: 12px;
    }
  }
`;

const ProgressText = styled.span`
  color: var(--primary-light);
  font-size: 14px;
  font-weight: 500;
`;

const ToggleSectionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--text-light);
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 500;
  cursor: pointer;
  margin: 10px 0;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.2);
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: var(--background-dark);
  border-radius: 10px;
  padding: 30px;
  max-width: 500px;
  width: 100%;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 20px;
  color: ${props => props.danger ? '#dc3545' : 'white'};
`;

const ModalText = styled.p`
  margin-bottom: 30px;
  color: var(--text-dim);
  line-height: 1.6;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
`;

const ProfilePage = () => {
  const { currentUser, updateProfile, loading: authLoading, logout } = useContext(AuthContext);
  const { watchlist, loading: watchlistLoading } = useContext(WatchlistContext);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    avatar: ''
  });
  const [linkCopied, setLinkCopied] = useState(false);
  const [watchHistory, setWatchHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showDeleteHistoryModal, setShowDeleteHistoryModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [reportsVisible, setReportsVisible] = useState(false);
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const navigate = useNavigate();

  // Initialize form data when user changes
  React.useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.name || '',
        bio: currentUser.bio || '',
        avatar: currentUser.avatar || 'default.jpeg'
      });
    }
  }, [currentUser]);

  // Fetch watch history
  useEffect(() => {
    const fetchWatchHistory = async () => {
      if (currentUser && historyVisible) {
        try {
          setLoadingHistory(true);
          const response = await UserAPI.getWatchHistory();
          if (response.data.success) {
            setWatchHistory(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching watch history:', error);
        } finally {
          setLoadingHistory(false);
        }
      }
    };

    fetchWatchHistory();
  }, [currentUser, historyVisible]);

  if (!currentUser) {
    return (
      <ProfileContainer>
        <EmptyState>
          <h3>Not Logged In</h3>
          <p>Please log in to view your profile.</p>
        </EmptyState>
      </ProfileContainer>
    );
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAvatarSelect = (avatar) => {
    setFormData({
      ...formData,
      avatar
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await updateProfile(formData);
      setIsEditing(false);
    } catch (err) {
      // handled in the context
    }
  };

  const copyProfileLink = () => {
    const url = `${window.location.origin}/profile/${currentUser.username}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleDeleteWatchHistory = async () => {
    try {
      setDeleteLoading(true);
      const response = await UserAPI.deleteWatchHistory();
      if (response.data.success) {
        setWatchHistory([]);
        setShowDeleteHistoryModal(false);
      }
    } catch (error) {
      console.error('Error deleting watch history:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleteLoading(true);
      const response = await UserAPI.deleteAccount();
      if (response.data.success) {
        logout();
        navigate('/');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Format date to readable format with IST adjustment
  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Unknown date';
    }
    
    try {
      // Parse the UTC date string
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Unknown date';
      }
      
      // Add 5 hours and 30 minutes for IST adjustment
      const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
      
      // Format using browser's locale settings with IST time
      return istDate.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) + ' IST';
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Unknown date';
    }
  };

  // Format progress percentage
  const formatProgress = (progress) => {
    if (progress === 100) return "100%";
    return `${Math.round(progress)}%`;
  };

  return (
    <ProfileContainer>
      <ProfileHeader>
        <AvatarContainer>
          {currentUser.avatar ? (
            <img src={`/avatars/${currentUser.avatar}`} alt="User avatar" />
          ) : (
            <FontAwesomeIcon icon={faUser} />
          )}
        </AvatarContainer>
        
        <UserInfo>
          <Username>@{currentUser.username}</Username>
          {currentUser.email && (
            <Email>
              <FontAwesomeIcon icon={faEnvelope} />
              {currentUser.email}
            </Email>
          )}
          {currentUser.name && <FullName>{currentUser.name}</FullName>}
          {currentUser.bio && <Bio>{currentUser.bio}</Bio>}
          
          <ShareableLink>
            <span>Profile URL:</span>
            <div className="link" onClick={copyProfileLink}>
              {window.location.origin}/profile/{currentUser.username}
              <FontAwesomeIcon icon={linkCopied ? faCheck : faCopy} />
            </div>
          </ShareableLink>
          
          {!isEditing && (
            <EditButton onClick={() => setIsEditing(true)}>
              <FontAwesomeIcon icon={faEdit} /> Edit Profile
            </EditButton>
          )}
        </UserInfo>
      </ProfileHeader>
      
      {isEditing && (
        <EditForm onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Name</Label>
            <Input 
              type="text" 
              name="name" 
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Bio</Label>
            <Textarea 
              name="bio" 
              value={formData.bio}
              onChange={handleChange}
              placeholder="Tell us about yourself"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Avatar</Label>
            <AvatarSelector>
              {AVATARS.map((avatar) => (
                <AvatarOption 
                  key={avatar}
                  selected={formData.avatar === avatar}
                  onClick={() => handleAvatarSelect(avatar)}
                >
                  <img src={`/avatars/${avatar}`} alt={`Avatar option ${avatar}`} />
                </AvatarOption>
              ))}
            </AvatarSelector>
          </FormGroup>
          
          <ButtonGroup>
            <SaveButton type="submit" disabled={authLoading}>
              {authLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} /> Save Changes
                </>
              )}
            </SaveButton>
            
            <CancelButton type="button" onClick={() => setIsEditing(false)}>
              <FontAwesomeIcon icon={faTimes} /> Cancel
            </CancelButton>
          </ButtonGroup>
        </EditForm>
      )}
      
      <SectionTitle>
        My Watchlist
      </SectionTitle>
      
      {watchlistLoading ? (
        <div className="loading-spinner" />
      ) : watchlist.length > 0 ? (
        <WatchlistGrid>
          {watchlist.map((item) => (
            <ContentCard 
              key={`${item.type}-${item.id}`}
              item={{
                _id: item.id,
                title: item.title,
                image: item.image,
                releaseYear: item.year,
                type: item.type
              }}
              type={item.type}
            />
          ))}
        </WatchlistGrid>
      ) : (
        <EmptyState>
          <h3>Your watchlist is empty</h3>
          <p>Start adding movies and shows to your watchlist to keep track of what you want to watch.</p>
        </EmptyState>
      )}
      
      <SectionTitle>
        <div>
          <FontAwesomeIcon icon={faFlag} /> My Reports
        </div>
      </SectionTitle>
      
      <ToggleSectionButton onClick={() => setReportsVisible(!reportsVisible)}>
        {reportsVisible ? 'Hide Reports' : 'Show Reports'}
        <FontAwesomeIcon icon={reportsVisible ? faChevronUp : faChevronDown} />
      </ToggleSectionButton>
      
      {reportsVisible && <UserReports />}
      
      <SectionTitle>
        <div>
          <FontAwesomeIcon icon={faLightbulb} /> My Episode Suggestions
        </div>
      </SectionTitle>
      
      <ToggleSectionButton onClick={() => setSuggestionsVisible(!suggestionsVisible)}>
        {suggestionsVisible ? 'Hide Suggestions' : 'Show Suggestions'}
        <FontAwesomeIcon icon={suggestionsVisible ? faChevronUp : faChevronDown} />
      </ToggleSectionButton>
      
      {suggestionsVisible && <UserSuggestions />}
      
      <SectionTitle>
        <div>
          <FontAwesomeIcon icon={faHistory} /> Watch History
        </div>
        {watchHistory.length > 0 && historyVisible && (
          <ActionButton onClick={() => setShowDeleteHistoryModal(true)} danger>
            <FontAwesomeIcon icon={faTrash} /> Clear History
          </ActionButton>
        )}
      </SectionTitle>
      
      <ToggleSectionButton onClick={() => setHistoryVisible(!historyVisible)}>
        {historyVisible ? 'Hide Watch History' : 'Show Watch History'}
        <FontAwesomeIcon icon={historyVisible ? faChevronUp : faChevronDown} />
      </ToggleSectionButton>
      
      {historyVisible && (
        <>
          {loadingHistory ? (
            <div className="loading-spinner" />
          ) : watchHistory.length > 0 ? (
            <WatchHistoryGrid>
              {watchHistory.map((item) => (
                <WatchHistoryItem key={`${item.type}-${item.id}`}>
                  <WatchHistoryHeader>
                    <WatchHistoryInfo>
                      <WatchHistoryTitle>
                        <span>
                          {item.type === 'movie' ? item.title : `${item.showTitle}: S${item.seasonNumber} E${item.episodeNumber} - ${item.title}`}
                        </span>
                      </WatchHistoryTitle>
                      <WatchHistoryMeta>
                        <span>
                          <FontAwesomeIcon icon={faEye} /> {item.completed ? 'Watched' : 'In Progress'}
                        </span>
                        <span>
                          <FontAwesomeIcon icon={faHistory} /> {formatDate(item.watchedAt)}
                        </span>
                        {!item.completed && (
                          <ProgressText>
                            Progress: {formatProgress(item.progress)}
                          </ProgressText>
                        )}
                      </WatchHistoryMeta>
                    </WatchHistoryInfo>
                  </WatchHistoryHeader>
                </WatchHistoryItem>
              ))}
            </WatchHistoryGrid>
          ) : (
            <EmptyState>
              <h3>No watch history</h3>
              <p>Your watch history will appear here after you start watching content.</p>
            </EmptyState>
          )}
        </>
      )}
      
      <UserComments isSelf={true} />
      
      <DangerSection>
        <DangerTitle>
          <FontAwesomeIcon icon={faExclamationTriangle} /> Account Management
        </DangerTitle>
        <DangerText>
          The actions below are irreversible. Please proceed with caution.
        </DangerText>
        <DangerButtonGroup>
          <ActionButton danger onClick={() => setShowDeleteAccountModal(true)}>
            <FontAwesomeIcon icon={faTrash} /> Delete Account
          </ActionButton>
        </DangerButtonGroup>
      </DangerSection>

      {showDeleteHistoryModal && (
        <Modal>
          <ModalContent>
            <ModalTitle danger>Delete Watch History</ModalTitle>
            <ModalText>
              Are you sure you want to delete your entire watch history? This action cannot be undone.
            </ModalText>
            <ModalButtons>
              <CancelButton onClick={() => setShowDeleteHistoryModal(false)}>
                Cancel
              </CancelButton>
              <ActionButton 
                danger 
                onClick={handleDeleteWatchHistory}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Deleting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} /> Delete
                  </>
                )}
              </ActionButton>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}

      {showDeleteAccountModal && (
        <Modal>
          <ModalContent>
            <ModalTitle danger>Delete Account</ModalTitle>
            <ModalText>
              Are you sure you want to delete your account? This will permanently remove all your data, including your profile, watchlist, and watch history. This action cannot be undone.
            </ModalText>
            <ModalButtons>
              <CancelButton onClick={() => setShowDeleteAccountModal(false)}>
                Cancel
              </CancelButton>
              <ActionButton 
                danger 
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Deleting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} /> Delete Account
                  </>
                )}
              </ActionButton>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}
    </ProfileContainer>
  );
};

export default ProfilePage;